import type { PurchaseOrder } from "@/redistribution/models/PurchaseOrder.js";

/**
 * Pool of related PO numbers for demo data generation
 */
const DEMO_RELATED_PO_POOL = [
  "1709268KCN",
  "1711590EHU",
  "1710946WOT",
  "1710960WOT",
  "1710962WOT",
  "1711207WOT",
  "1711208WOT",
  "1712621WOT",
  "1712622WOT",
  "1712624WOT",
  "1712625WOT",
  "1712626WOT",
  "1712628WOT",
  "1712631WOT",
  "1712633WOT",
  "1712635WOT",
  "1712636WOT",
  "1712637WOT",
  "1712638WOT",
  "1712639WOT",
  "1711463KGR",
  "1711465KGR",
  "1708202WOT",
  "1708203WOT",
  "1709268/1KCN",
  "1707062KGR",
  "1712349KGR",
  "1704705KGR",
  "1707715KCN",
  "1709220KCN",
  "1709219KCN",
  "1707721KCN",
  "1709267KCN",
  "1709221KCN",
  "1711681KCN",
  "1707720KCN",
  "1709539KCN",
  "1711664KCN",
  "1711618KCN",
  "1709422EHU",
  "1709467KCN",
  "1708053EHU",
  "1708052EHU",
  "1708046EHU",
  "1706462/1KCN",
  "1708044EHU",
  "1709457KCN",
  "1711672KCN",
  "1711671KCN",
] as const;

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function randomInt(min: number, max: number): number {
  const lower = Math.ceil(min);
  const upper = Math.floor(max);
  return Math.floor(Math.random() * (upper - lower + 1)) + lower;
}

function randomizeIsoDate(
  baseIso: string,
  minDayOffset: number,
  maxDayOffset: number
): string {
  const base = new Date(baseIso);
  if (Number.isNaN(base.getTime())) {
    return baseIso;
  }

  const offset = randomInt(minDayOffset, maxDayOffset);
  base.setDate(base.getDate() + offset);
  return base.toISOString();
}

export function createDemoPrimaryPurchaseOrder(
  poNumber: string
): PurchaseOrder {
  const today = new Date();
  const baseArrival = new Date(today);
  baseArrival.setDate(baseArrival.getDate() + randomInt(7, 30));
  const baseArrivalIso = baseArrival.toISOString();

  const demoSkuSeeds = [
    {
      skuNumber: "DEMO-1001",
      description: "Demo Sofa",
      color: "Charcoal",
      theme: "Modern",
      cubeSize: 45,
      qty: 12,
      stock: 80,
    },
    {
      skuNumber: "DEMO-1002",
      description: "Demo Loveseat",
      color: "Charcoal",
      theme: "Modern",
      cubeSize: 32,
      qty: 8,
      stock: 50,
    },
    {
      skuNumber: "DEMO-1003",
      description: "Demo Recliner",
      color: "Sand",
      theme: "Modern",
      cubeSize: 28,
      qty: 15,
      stock: 20,
    },
    {
      skuNumber: "DEMO-1004",
      description: "Demo Ottoman",
      color: "Sand",
      theme: "Modern",
      cubeSize: 12,
      qty: 20,
      stock: 95,
    },
    {
      skuNumber: "DEMO-1005",
      description: "Demo Accent Chair",
      color: "Indigo",
      theme: "Modern",
      cubeSize: 22,
      qty: 10,
      stock: 35,
    },
  ];

  const skus = demoSkuSeeds.map((seed: (typeof demoSkuSeeds)[number]) => {
    const lineItemQuantity = clampNumber(
      seed.qty + randomInt(-2, 6),
      1,
      99_999
    );
    const forecastQuantityAvailable = seed.stock + lineItemQuantity;
    return {
      skuNumber: seed.skuNumber,
      pmKey: seed.skuNumber,
      description: seed.description,
      lineItemQuantity,
      quantityAvailable: seed.stock,
      dailyRateOfSale: 2,
      daysToStockout: Math.floor(seed.stock / 2),
      urgency: 0,
      forecastQuantityAvailable,
      adjustedForecast: forecastQuantityAvailable,
      theme: seed.theme,
      cubeSize: seed.cubeSize,
      pieceCount: 1,
      color: seed.color,
      family: seed.skuNumber.slice(0, 4),
      stockedThroughDate: "",
      difference: 0,
    };
  });

  return {
    vendorCode: "DEMO",
    arrivalDate: baseArrivalIso,
    shipDate: baseArrivalIso,
    division: "FL",
    purchaseOrderNumber: poNumber,
    purchaseOrderRequestId: poNumber,
    warehouse: 99,
    purchaseOrderStatus: "Open",
    countUniqueSkus: skus.length,
    sumQuantities: skus.reduce((total, sku) => total + sku.lineItemQuantity, 0),
    cubeTotal: skus.reduce(
      (total, sku) => total + sku.cubeSize * sku.lineItemQuantity,
      0
    ),
    calcArrivalDate: baseArrivalIso,
    skus,
  };
}

export function createDemoRelatedPurchaseOrders(
  primaryPurchaseOrder: PurchaseOrder
): PurchaseOrder[] {
  const shuffledPool = DEMO_RELATED_PO_POOL.filter(
    (poNumber) => poNumber !== primaryPurchaseOrder.purchaseOrderNumber
  ).toSorted(() => Math.random() - 0.5);

  const targetCount = randomInt(3, Math.min(50, shuffledPool.length));
  const selectedPoNumbers = shuffledPool.slice(0, targetCount);

  return selectedPoNumbers.map((poNumber) => {
    const demoSkus = primaryPurchaseOrder.skus.map(
      (sku: PurchaseOrder["skus"][number]) => {
        const quantityDelta = randomInt(-2, 6);
        const forecastDelta = randomInt(-8, 8);
        const nextLineItemQuantity = clampNumber(
          sku.lineItemQuantity + quantityDelta,
          1,
          99_999
        );
        return {
          ...sku,
          lineItemQuantity: nextLineItemQuantity,
          forecastQuantityAvailable:
            sku.forecastQuantityAvailable + forecastDelta,
        };
      }
    );

    const sumQuantities = demoSkus.reduce(
      (total: number, sku: PurchaseOrder["skus"][number]) =>
        total + sku.lineItemQuantity,
      0
    );
    const cubeTotal = demoSkus.reduce(
      (total: number, sku: PurchaseOrder["skus"][number]) =>
        total + sku.cubeSize * sku.lineItemQuantity,
      0
    );

    return {
      ...primaryPurchaseOrder,
      purchaseOrderNumber: poNumber,
      shipDate: randomizeIsoDate(primaryPurchaseOrder.shipDate, -5, 5),
      arrivalDate: randomizeIsoDate(primaryPurchaseOrder.arrivalDate, -3, 10),
      calcArrivalDate: randomizeIsoDate(
        primaryPurchaseOrder.calcArrivalDate ||
          primaryPurchaseOrder.arrivalDate,
        -3,
        10
      ),
      sumQuantities,
      cubeTotal,
      skus: demoSkus,
      purchaseOrderRequestId:
        primaryPurchaseOrder.purchaseOrderRequestId ??
        primaryPurchaseOrder.purchaseOrderNumber,
    };
  });
}
